import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildContextUrl } from '../utils/contextUrl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageSquare, X, Send, Paperclip, Mic } from 'lucide-react';

// ── Markdown custom components ────────────────────────────────────────────────
const mdComponents = {
    table: ({ children }) => (
        <div className="overflow-x-auto my-2 rounded-lg border border-gray-200">
            <table className="min-w-full text-xs">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
            {children}
        </th>
    ),
    td: ({ children }) => <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{children}</td>,
    ul: ({ children }) => <ul className="list-disc list-outside ml-4 my-1 space-y-0.5">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-outside ml-4 my-1 space-y-0.5">{children}</ol>,
    li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
    p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    code: ({ inline, children }) =>
        inline
            ? <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
            : <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs font-mono overflow-x-auto my-2"><code>{children}</code></pre>,
    h1: ({ children }) => <h1 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h1>,
    h2: ({ children }) => <h2 className="text-sm font-bold mb-1.5 mt-2.5 first:mt-0">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1 mt-2 first:mt-0">{children}</h3>,
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-blue-400 pl-3 text-gray-600 italic my-2">{children}</blockquote>
    ),
};

// ── Sub-components ────────────────────────────────────────────────────────────
function TypingIndicator() {
    return (
        <div className="flex items-end gap-2">
            <BotAvatar />
            <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
        </div>
    );
}

function BotAvatar() {
    return (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-white text-xs font-bold">R</span>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AgentChat({ usuario }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [threadId, setThreadId] = useState('');
    const [pendingImages, setPendingImages] = useState([]);
    const [showCSAT, setShowCSAT] = useState(false);
    const [csatScore, setCsatScore] = useState(0);
    const [csatSubmitted, setCsatSubmitted] = useState(false);
    const [token, setToken] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    const navigate = useNavigate();
    const scrollAnchorRef = useRef(null);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);

    const agentUrl = window.REACT_APP_AGENT_ENDPOINT;
    const apiUrl = window.REACT_APP_API_ENDPOINT;

    useEffect(() => {
        if (!usuario) return;
        const tid = `web_${usuario.id}_${new Date().toISOString().slice(0, 10)}`;
        setThreadId(tid);

        fetch(`${apiUrl}/api/agent/token`, { credentials: 'include' })
            .then(r => r.json())
            .then(({ token: t }) => {
                setToken(t);
                return fetch(`${agentUrl}/agent/chat/history?thread_id=${tid}`, {
                    headers: { Authorization: `Bearer ${t}` }
                });
            })
            .then(r => r.ok ? r.json() : { messages: [] })
            .then(({ messages: hist }) => {
                if (hist && hist.length > 0) {
                    setMessages(hist.map(m => ({ ...m, id: crypto.randomUUID() })));
                }
            })
            .catch(() => {});
    }, [usuario, agentUrl, apiUrl]);

    useEffect(() => {
        scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [messages, isLoading]);

    const sendMessage = async () => {
        if (!inputText.trim() && pendingImages.length === 0) return;
        if (!token) return;

        const userMsg = { role: 'user', content: inputText, id: crypto.randomUUID() };
        setMessages(prev => [...prev, userMsg]);
        const sentText = inputText;
        const sentImages = [...pendingImages];
        setInputText('');
        setPendingImages([]);
        setIsLoading(true);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        const assistantMsgId = crypto.randomUUID();
        setMessages(prev => [...prev, { role: 'assistant', content: '', id: assistantMsgId, contextCards: [] }]);

        try {
            const response = await fetch(`${agentUrl}/agent/chat/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    message: sentText,
                    thread_id: threadId,
                    images: sentImages.map(({ data, media_type }) => ({ data, media_type }))
                })
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const event = JSON.parse(line.slice(6));
                    if (event.type === 'token') {
                        setMessages(prev => prev.map(m =>
                            m.id === assistantMsgId ? { ...m, content: m.content + event.content } : m
                        ));
                    } else if (event.type === 'context') {
                        setMessages(prev => prev.map(m =>
                            m.id === assistantMsgId
                                ? { ...m, contextCards: [...(m.contextCards || []), event] }
                                : m
                        ));
                    } else if (event.type === 'done') {
                        setIsLoading(false);
                        if (!isOpen) setUnreadCount(c => c + 1);
                    } else if (event.type === 'error') {
                        setIsLoading(false);
                        setMessages(prev => prev.map(m =>
                            m.id === assistantMsgId
                                ? { ...m, content: 'Lo siento, ocurrió un error. Inténtalo de nuevo.' }
                                : m
                        ));
                    }
                }
            }
        } catch {
            setIsLoading(false);
            setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                    ? { ...m, content: 'Lo siento, ocurrió un error. Inténtalo de nuevo.' }
                    : m
            ));
        }
    };

    const handleImageSelect = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) return;
        if (file.size > 10 * 1024 * 1024) { alert('Máximo 10MB por imagen'); return; }
        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target.result;
            const [prefix, base64] = dataUrl.split(',');
            const media_type = prefix.match(/:(.*?);/)[1];
            setPendingImages(prev => [...prev, { data: base64, media_type, preview: dataUrl }]);
        };
        reader.readAsDataURL(file);
    };

    const handleTextareaInput = (e) => {
        setInputText(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    };

    const handleClose = () => {
        if (messages.some(m => m.role === 'assistant') && !showCSAT && !csatSubmitted) {
            setShowCSAT(true);
        } else {
            setIsOpen(false);
            setShowCSAT(false);
        }
    };

    const submitCSAT = async (score) => {
        setCsatScore(score);
        setCsatSubmitted(true);
        setShowCSAT(false);
        setIsOpen(false);
        if (token) {
            fetch(`${agentUrl}/agent/chat/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ thread_id: threadId, score })
            }).catch(() => {});
        }
    };

    const toggleOpen = () => {
        setIsOpen(o => !o);
        setUnreadCount(0);
    };

    if (!usuario) return null;

    const canSend = !isLoading && (inputText.trim().length > 0 || pendingImages.length > 0);

    return (
        <>
            {/* ── Floating button ── */}
            <button
                onClick={toggleOpen}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)' }}
                title="Asistente Reformista"
            >
                <MessageSquare size={22} className="text-white" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                        {unreadCount}
                    </span>
                )}
            </button>

            {/* ── Chat panel ── */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 z-50 flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
                    style={{ width: '420px', height: '600px' }}>

                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)' }}>
                        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">R</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold text-sm leading-tight">Asistente Reformista</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                <span className="text-blue-100 text-xs">Disponible ahora</span>
                            </div>
                        </div>
                        <button onClick={handleClose}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0">
                            <X size={16} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
                        style={{ background: '#f8fafc' }}>
                        {messages.length === 0 && (
                            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                                    style={{ background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)' }}>
                                    <MessageSquare size={20} className="text-white" />
                                </div>
                                <div>
                                    <p className="text-gray-700 font-medium text-sm">¡Hola! Soy tu asistente IA</p>
                                    <p className="text-gray-400 text-xs mt-1">Pregúntame sobre propiedades, reformas o inversiones</p>
                                </div>
                            </div>
                        )}
                        {messages.map(msg => (
                            <div key={msg.id} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {msg.role === 'assistant' && <BotAvatar />}
                                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm ${
                                    msg.role === 'user'
                                        ? 'rounded-2xl rounded-br-sm text-white'
                                        : 'rounded-2xl rounded-bl-sm bg-white border border-gray-100 shadow-sm text-gray-800'
                                }`}
                                    style={msg.role === 'user'
                                        ? { background: 'linear-gradient(135deg, #2563eb 0%, #4338ca 100%)' }
                                        : {}}>
                                    {msg.role === 'assistant'
                                        ? <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={mdComponents}
                                          >{msg.content || '…'}</ReactMarkdown>
                                        : <span className="leading-relaxed">{msg.content}</span>}
                                    {msg.role === 'assistant' && msg.contextCards?.length > 0 && (
                                        <div className="mt-2 flex flex-col gap-1.5">
                                            {msg.contextCards.map((card, i) => {
                                                const url = buildContextUrl(card);
                                                const isMobile = window.innerWidth < 768;
                                                return (
                                                    <div key={i} className="flex flex-col gap-1">
                                                        <span className="text-xs text-gray-400">{card.label}</span>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => isMobile ? window.open(url, '_blank') : navigate(url)}
                                                                className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors"
                                                            >Ver aquí</button>
                                                            <button
                                                                onClick={() => window.open(url, '_blank')}
                                                                className="text-xs px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 font-medium transition-colors"
                                                            >Ver en nueva pestaña</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isLoading && <TypingIndicator />}
                        <div ref={scrollAnchorRef} />
                    </div>

                    {/* Pending images */}
                    {pendingImages.length > 0 && (
                        <div className="px-4 py-2 flex gap-2 flex-wrap border-t border-gray-100 bg-white flex-shrink-0">
                            {pendingImages.map((img, i) => (
                                <div key={i} className="relative">
                                    <img src={img.preview} alt="" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                                    <button
                                        onClick={() => setPendingImages(prev => prev.filter((_, idx) => idx !== i))}
                                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-700 text-white rounded-full text-xs flex items-center justify-center leading-none hover:bg-red-500 transition-colors"
                                    ><X size={9} /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="px-3 py-3 border-t border-gray-100 bg-white flex-shrink-0">
                        <div className="flex items-end gap-2 bg-gray-50 rounded-xl border border-gray-200 px-3 py-2 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
                            <textarea
                                ref={textareaRef}
                                value={inputText}
                                onChange={handleTextareaInput}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                                }}
                                placeholder="Escribe un mensaje…"
                                rows={1}
                                className="flex-1 resize-none bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none leading-relaxed"
                                style={{ maxHeight: '120px' }}
                            />
                            <div className="flex items-center gap-1 flex-shrink-0 pb-0.5">
                                <input type="file" ref={fileInputRef} accept="image/*" className="hidden"
                                    onChange={e => handleImageSelect(e.target.files[0])} />
                                <button onClick={() => fileInputRef.current.click()}
                                    className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-blue-500 rounded-lg transition-colors"
                                    title="Adjuntar imagen">
                                    <Paperclip size={16} />
                                </button>
                                <button disabled title="Próximamente: grabación de voz"
                                    className="w-7 h-7 flex items-center justify-center text-gray-300 cursor-not-allowed rounded-lg">
                                    <Mic size={16} />
                                </button>
                                <button onClick={sendMessage} disabled={!canSend}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={canSend ? { background: 'linear-gradient(135deg, #2563eb 0%, #4338ca 100%)' } : { background: '#e5e7eb' }}>
                                    <Send size={14} className={canSend ? 'text-white' : 'text-gray-400'} />
                                </button>
                            </div>
                        </div>
                        <p className="text-center text-gray-300 text-xs mt-1.5">Enter para enviar · Shift+Enter para nueva línea</p>
                    </div>
                </div>
            )}

            {/* ── CSAT modal ── */}
            {showCSAT && (
                <div className="fixed bottom-24 right-6 z-50 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100"
                    style={{ width: '320px' }}>
                    <p className="text-center text-gray-700 font-semibold text-sm mb-1">¿Cómo fue tu experiencia?</p>
                    <p className="text-center text-gray-400 text-xs mb-4">Tu valoración nos ayuda a mejorar</p>
                    <div className="flex justify-center gap-2 mb-5">
                        {[1, 2, 3, 4, 5].map(n => (
                            <button key={n} onClick={() => submitCSAT(n)}
                                onMouseEnter={() => setCsatScore(n)}
                                onMouseLeave={() => setCsatScore(0)}
                                className={`text-3xl transition-transform hover:scale-110 ${csatScore >= n ? 'text-yellow-400' : 'text-gray-200'}`}>
                                ★
                            </button>
                        ))}
                    </div>
                    <button onClick={() => { setShowCSAT(false); setIsOpen(false); }}
                        className="w-full text-xs text-gray-400 hover:text-gray-600 py-1 transition-colors">
                        Omitir
                    </button>
                </div>
            )}
        </>
    );
}
